import React, { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useGetAllUsers, usePatchUser } from '../../../api/routes/users'
import { useGetAllBusinesses } from '../../../api/routes/businesses'
import { Button } from '../../../components/ui/button'
import { queryClient } from '../../../api/query-client'
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
    DialogFooter,
} from '../../../components/ui/dialog'
import { authClient, hasPermission } from '../../../lib/auth-client'
import { toast } from '../../../lib/toast'
import { Warning } from '@mui/icons-material'
import { ArrowLeft } from 'lucide-react'
import RecordLogs from '../../../components/shared/record-logs'

const UserDetailsPage: React.FC = () => {
    const { id } = useParams<{ id: string }>()
    const navigate = useNavigate()
    const { data: users } = useGetAllUsers()
    const { data: session } = authClient.useSession()
    const currentUser = session?.user as any
    const { data: businesses } = useGetAllBusinesses()
    const user = users?.find((u: any) => u.id === id)
    const [editMode, setEditMode] = useState(false)
    const [editUser, setEditUser] = useState<any>(user)
    const [errorDialogOpen, setErrorDialogOpen] = useState(false)
    const [errorMessage, setErrorMessage] = useState('')
    const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false)
    const [impersonateLoading, setImpersonateLoading] = useState(false)

    useEffect(() => {
        setEditUser(user)
    }, [user])

    const patchUser = usePatchUser()
    const [deleteLoading, setDeleteLoading] = useState(false)

    if (!user) {
        return (
            <div className="mx-auto py-6 lg:py-10 px-4 sm:px-6 lg:px-8 xl:px-32">
                <div className="rounded-lg border border-dashed p-12 text-center">
                    <p className="text-sm text-muted-foreground">
                        User not found
                    </p>
                </div>
            </div>
        )
    }

    const isSelf = currentUser?.id === user.id
    const isImpersonating = Boolean((session?.session as any)?.impersonatedBy)
    const canImpersonate =
        currentUser &&
        hasPermission(currentUser.role, { user: ['impersonate'] }) &&
        !isSelf &&
        !isImpersonating

    const handleEditSave = () => {
        if (!editUser || !id) return
        patchUser.mutate(
            {
                id,
                data: {
                    firstName: editUser.firstName,
                    lastName: editUser.lastName,
                    email: editUser.email,
                    role: editUser.role,
                    isActive: editUser.isActive,
                    businessId: editUser.businessId,
                },
            },
            {
                onSuccess: () => {
                    setEditMode(false)
                },
                onError: (error: any) => {
                    setErrorMessage(
                        error?.response?.data?.message ||
                            error.message ||
                            'Unknown error'
                    )
                    setErrorDialogOpen(true)
                },
            }
        )
    }

    const handleDelete = async () => {
        if (!id) return
        setDeleteLoading(true)
        const { error } = await authClient.admin.removeUser({ userId: id })
        setDeleteLoading(false)
        if (error) {
            setDeleteConfirmOpen(false)
            setErrorMessage(error.message || 'Unknown error')
            setErrorDialogOpen(true)
        } else {
            queryClient.invalidateQueries({ queryKey: ['users'] })
            navigate('/users')
        }
    }

    const handleImpersonate = async () => {
        if (!id) return

        setImpersonateLoading(true)
        const { error } = await authClient.admin.impersonateUser({
            userId: id,
        })
        setImpersonateLoading(false)

        if (error) {
            setErrorMessage(error.message || 'Failed to impersonate user')
            setErrorDialogOpen(true)
            return
        }

        toast.success(`Now impersonating ${user.firstName} ${user.lastName}`)
        queryClient.clear()
        window.location.href = '/dashboard'
    }

    return (
        <div className="mx-auto py-6 lg:py-10 px-4 sm:px-6 lg:px-8 xl:px-32">
            <div className="flex items-center gap-4 mb-6">
                <button
                    onClick={() => navigate('/users')}
                    className="p-1.5 rounded-md hover:bg-accent text-muted-foreground hover:text-foreground transition-colors"
                >
                    <ArrowLeft className="h-4 w-4" />
                </button>
                <div className="flex-1">
                    <h1 className="text-2xl font-semibold tracking-tight text-foreground">
                        {user.firstName} {user.lastName}
                    </h1>
                    <p className="text-sm text-muted-foreground font-mono">
                        {user.email}
                    </p>
                </div>
                <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setEditMode((v) => !v)}
                >
                    {editMode ? 'Cancel' : 'Edit'}
                </Button>
                {canImpersonate && (
                    <Button
                        size="sm"
                        onClick={handleImpersonate}
                        disabled={impersonateLoading}
                    >
                        {impersonateLoading
                            ? 'Impersonating...'
                            : 'Impersonate User'}
                    </Button>
                )}
            </div>

            {/* User Details */}
            <div className="rounded-lg border bg-card mb-6">
                <div className="px-6 py-4 border-b">
                    <h3 className="text-sm font-medium text-muted-foreground uppercase tracking-wider">
                        User Details
                    </h3>
                </div>
                {editMode ? (
                    <div className="p-6 space-y-4">
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <div className="space-y-1.5">
                                <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                                    First Name
                                </label>
                                <input
                                    className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm focus:outline-none focus:ring-1 focus:ring-ring"
                                    value={editUser?.firstName || ''}
                                    onChange={(e) =>
                                        setEditUser((u: any) => ({
                                            ...u,
                                            firstName: e.target.value,
                                        }))
                                    }
                                />
                            </div>
                            <div className="space-y-1.5">
                                <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                                    Last Name
                                </label>
                                <input
                                    className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm focus:outline-none focus:ring-1 focus:ring-ring"
                                    value={editUser?.lastName || ''}
                                    onChange={(e) =>
                                        setEditUser((u: any) => ({
                                            ...u,
                                            lastName: e.target.value,
                                        }))
                                    }
                                />
                            </div>
                        </div>
                        <div className="space-y-1.5">
                            <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                                Email
                            </label>
                            <input
                                type="email"
                                className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm focus:outline-none focus:ring-1 focus:ring-ring"
                                value={editUser?.email || ''}
                                onChange={(e) =>
                                    setEditUser((u: any) => ({
                                        ...u,
                                        email: e.target.value,
                                    }))
                                }
                            />
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <div className="space-y-1.5">
                                <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                                    Role
                                </label>
                                <select
                                    className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm focus:outline-none focus:ring-1 focus:ring-ring"
                                    value={editUser?.role || 'user'}
                                    onChange={(e) =>
                                        setEditUser((u: any) => ({
                                            ...u,
                                            role: e.target.value,
                                        }))
                                    }
                                >
                                    <option value="user">User</option>
                                    <option value="businessAdmin">
                                        Business Admin
                                    </option>
                                    <option value="systemAdmin">
                                        System Admin
                                    </option>
                                </select>
                            </div>
                            <div className="space-y-1.5">
                                <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                                    Status
                                </label>
                                <select
                                    className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm focus:outline-none focus:ring-1 focus:ring-ring"
                                    value={
                                        editUser?.isActive
                                            ? 'active'
                                            : 'inactive'
                                    }
                                    onChange={(e) =>
                                        setEditUser((u: any) => ({
                                            ...u,
                                            isActive:
                                                e.target.value === 'active',
                                        }))
                                    }
                                >
                                    <option value="active">Active</option>
                                    <option value="inactive">
                                        Inactive
                                    </option>
                                </select>
                            </div>
                        </div>
                        <div className="space-y-1.5">
                            <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                                Business
                            </label>
                            <select
                                className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm focus:outline-none focus:ring-1 focus:ring-ring"
                                value={editUser?.businessId || ''}
                                onChange={(e) =>
                                    setEditUser((u: any) => ({
                                        ...u,
                                        businessId:
                                            e.target.value || null,
                                    }))
                                }
                            >
                                <option value="">
                                    None (System-level)
                                </option>
                                {businesses?.map((business: any) => (
                                    <option
                                        key={business.id}
                                        value={business.id}
                                    >
                                        {business.name}
                                    </option>
                                ))}
                            </select>
                        </div>
                        <div className="pt-2">
                            <Button
                                size="sm"
                                onClick={handleEditSave}
                                disabled={patchUser.isPending}
                            >
                                {patchUser.isPending
                                    ? 'Saving...'
                                    : 'Save Changes'}
                            </Button>
                        </div>
                    </div>
                ) : (
                    <div className="p-6">
                        <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-5">
                            <div className="space-y-1">
                                <dt className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                                    First Name
                                </dt>
                                <dd className="text-sm text-foreground">
                                    {user.firstName}
                                </dd>
                            </div>
                            <div className="space-y-1">
                                <dt className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                                    Last Name
                                </dt>
                                <dd className="text-sm text-foreground">
                                    {user.lastName}
                                </dd>
                            </div>
                            <div className="space-y-1">
                                <dt className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                                    Email
                                </dt>
                                <dd className="text-sm text-foreground font-mono">
                                    {user.email}
                                </dd>
                            </div>
                            <div className="space-y-1">
                                <dt className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                                    Role
                                </dt>
                                <dd className="text-sm text-foreground capitalize">
                                    {user.role
                                        ?.replace(/([A-Z])/g, ' $1')
                                        .trim()}
                                </dd>
                            </div>
                            <div className="space-y-1">
                                <dt className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                                    Status
                                </dt>
                                <dd>
                                    {user.isActive ? (
                                        <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-green-50 text-green-700 border border-green-200">
                                            Active
                                        </span>
                                    ) : (
                                        <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-red-50 text-red-700 border border-red-200">
                                            Inactive
                                        </span>
                                    )}
                                </dd>
                            </div>
                            <div className="space-y-1">
                                <dt className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                                    Verified
                                </dt>
                                <dd>
                                    {user.emailVerified ? (
                                        <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-green-50 text-green-700 border border-green-200">
                                            Verified
                                        </span>
                                    ) : (
                                        <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 border border-amber-200">
                                            Pending
                                        </span>
                                    )}
                                </dd>
                            </div>
                            <div className="space-y-1">
                                <dt className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                                    Business
                                </dt>
                                <dd className="text-sm text-foreground">
                                    {user.business?.name || (
                                        <span className="text-muted-foreground">
                                            None (System-level)
                                        </span>
                                    )}
                                </dd>
                            </div>
                        </dl>
                    </div>
                )}
            </div>

            {/* User Activity Logs */}
            {currentUser && hasPermission(currentUser.role, { log: ['read'] }) && id && (
                <RecordLogs resourceType="user" resourceId={id} title="User Activity" />
            )}

            {/* Danger Zone */}
            {currentUser &&
                hasPermission(currentUser.role, { user: ['delete'] }) &&
                !isSelf && (
                    <div className="rounded-lg border border-destructive/30 bg-card">
                        <div className="px-6 py-4 border-b border-destructive/30">
                            <h3 className="text-sm font-medium text-destructive uppercase tracking-wider">
                                Danger Zone
                            </h3>
                        </div>
                        <div className="p-6 flex items-center justify-between">
                            <div className="space-y-0.5">
                                <p className="text-sm font-medium text-foreground">
                                    Delete this user
                                </p>
                                <p className="text-xs text-muted-foreground">
                                    Permanently remove this account. This
                                    cannot be undone.
                                </p>
                            </div>
                            <Button
                                variant="destructive"
                                size="sm"
                                onClick={() => setDeleteConfirmOpen(true)}
                                disabled={deleteLoading}
                            >
                                Delete User
                            </Button>
                        </div>
                    </div>
                )}

            {/* Error Dialog */}
            <Dialog
                open={errorDialogOpen}
                onOpenChange={setErrorDialogOpen}
            >
                <DialogContent className="sm:max-w-md">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <Warning className="text-destructive" />
                            Error
                        </DialogTitle>
                        <DialogDescription>{errorMessage}</DialogDescription>
                    </DialogHeader>
                    <DialogFooter>
                        <Button
                            onClick={() => setErrorDialogOpen(false)}
                        >
                            Close
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Delete Confirm Dialog */}
            <Dialog
                open={deleteConfirmOpen}
                onOpenChange={setDeleteConfirmOpen}
            >
                <DialogContent className="sm:max-w-md">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <Warning className="text-amber-500" />
                            Delete User
                        </DialogTitle>
                    </DialogHeader>
                    <div className="py-4">
                        <p className="text-sm text-foreground">
                            Are you sure you want to permanently delete{' '}
                            <strong>
                                {user.firstName} {user.lastName}
                            </strong>{' '}
                            ({user.email})? This action cannot be undone.
                        </p>
                    </div>
                    <DialogFooter className="flex justify-end gap-2">
                        <Button
                            variant="outline"
                            onClick={() => setDeleteConfirmOpen(false)}
                        >
                            Cancel
                        </Button>
                        <Button
                            variant="destructive"
                            onClick={handleDelete}
                            disabled={deleteLoading}
                        >
                            {deleteLoading
                                ? 'Deleting...'
                                : 'Delete User'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    )
}

export default UserDetailsPage
